import Keycloak from 'keycloak-js';
import { environment } from '@service/environment/environment';

const AUTH_TOKEN_KEY = 'apollo-token';

let instance: Keycloak | null = null;
let initPromise: Promise<Keycloak | null> | null = null;

export function getKeycloak(): Keycloak {
  if (!instance) {
    throw new Error('Keycloak has not been initialized');
  }
  return instance;
}

export function getStoredToken(): string {
  return localStorage.getItem(AUTH_TOKEN_KEY) || instance?.token || '';
}

export function persistToken(token?: string): void {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    return;
  }
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function refreshAccessToken(): Promise<string> {
  const keycloak = getKeycloak();
  const refreshed = await keycloak.updateToken(30);
  const token = keycloak.token || '';
  persistToken(token);
  if (!refreshed && !token) {
    throw new Error('Keycloak token refresh failed');
  }
  return token;
}

export async function logout(): Promise<void> {
  persistToken();
  if (!instance) {
    return;
  }
  await instance.logout();
}

export function userEmail(): string {
  const parsed = instance?.tokenParsed as { email?: string; preferred_username?: string } | undefined;
  const candidate = parsed?.email || parsed?.preferred_username || '';
  return candidate.includes('@') ? candidate : '';
}

export function userDisplayName(): string {
  const parsed = instance?.tokenParsed as {
    name?: string;
    preferred_username?: string;
    email?: string;
  } | undefined;
  return parsed?.name || parsed?.preferred_username || parsed?.email || 'GATT';
}

function isBrowserReachableAuthUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
      return true;
    }
    return hostname.includes('.');
  } catch {
    return false;
  }
}

export function isKeycloakConfigured(): boolean {
  return Boolean(
    environment.keycloakUrl
    && environment.keycloakRealm
    && environment.keycloakClientId
    && isBrowserReachableAuthUrl(environment.keycloakUrl),
  );
}

function authRedirectUri(): string {
  const url = new URL(window.location.href);
  if (url.hostname === '127.0.0.1') {
    url.hostname = 'localhost';
  }
  return `${url.origin}${url.pathname}`;
}

export async function initKeycloak(): Promise<Keycloak | null> {
  if (instance) {
    return instance;
  }
  if (!isKeycloakConfigured()) {
    console.warn('Keycloak is not configured; continuing without authentication');
    return null;
  }
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const keycloak = new Keycloak({
      realm: environment.keycloakRealm,
      url: environment.keycloakUrl,
      clientId: environment.keycloakClientId,
    });

    keycloak.onTokenExpired = () => {
      void keycloak.updateToken(30).then((ok) => {
        if (ok && keycloak.token) {
          persistToken(keycloak.token);
        }
      }).catch(() => {
        void keycloak.login();
      });
    };

    try {
      const authenticated = await keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        redirectUri: authRedirectUri(),
      });

      if (!authenticated || !keycloak.token) {
        await keycloak.login();
        throw new Error('Keycloak login is required');
      }

      persistToken(keycloak.token);
      instance = keycloak;
      return keycloak;
    } catch (error) {
      console.error('Keycloak initialization failed', error);
      if (typeof keycloak.logout === 'function') {
        try {
          await keycloak.logout();
        } catch {
          // ignore secondary logout errors
        }
      }
      throw error instanceof Error ? error : new Error('Keycloak initialization failed');
    }
  })();

  return initPromise;
}
